import { APIExtRoutes } from "@constants";
import { PortfolioInvestmentTypeSelection } from "@models/managed-accounts/entities/step/setup";
import {
  AnalysisDecisionDTO,
  AnalysisDTO,
  AnalysisFilterDTO,
} from "@models/platform-analysis/entities/analysis";
import { AnalysisBannerNameDTO } from "@models/platform-analysis/entities/bannerName";
import { DisclaimerDTO } from "@models/platform-analysis/entities/disclaimer";
import { AnalysisStepDTO } from "@models/platform-analysis/entities/step";
import { BusinessMetricCategories } from "@models/platform-analysis/entities/steps/businessMetric";
import {
  AnalysisConfigurationFeatureSectionDTO,
  AnalysisFeatureAllDataDTO,
  AnalysisGetFeatureSectionsDTO,
  FeatureAnalysisRowDTO,
  PlatformAnalysisFilter,
  SelectedProductDTO,
} from "@models/platform-analysis/entities/steps/feature";
import {
  AnalysisFeeCalculatorDTO,
  AnalysisFeePortfolioDTO,
  FeeSubProductDTO,
  PortfolioAssignmentValidationRequestDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { AnalysisSetupDTO } from "@models/platform-analysis/entities/steps/setup";
import { ProductRecommendationDTO } from "@models/platform-analysis/entities/steps/summary";
import { FeatureAnalysisCollapseStateFilterLevel } from "@models/platform-analysis/enums/feature/featureAnalysisCollapseLevelFilter";
import { FeatureAnalysisEssentialFilterLevel } from "@models/platform-analysis/enums/feature/featureAnalysisEssentialFilter";
import { FeatureAnalysisAvailabilityFilterLevel } from "@models/platform-analysis/enums/feature/featureAnalysisFilter";
import { FeesDisplayMode } from "@models/platform-analysis/enums/fee/displayMode";
import { FeesDisplayStyle } from "@models/platform-analysis/enums/fee/displayStyle";
import { FeeCalculatorSectionId } from "@models/platform-analysis/enums/fee/feeCalculatorSectionId";
import { FeesPerformanceScope } from "@models/platform-analysis/enums/fee/feesPerformanceScope";
import { FeeHeatmapMode } from "@models/platform-analysis/enums/fee/heatmapMode";
import { FeeInvestmentMenuOption } from "@models/platform-analysis/enums/fee/investmentMenu";
import { FeeMissingInvestmentsOption } from "@models/platform-analysis/enums/fee/missingInvestments";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { AnalysisSubStep } from "@models/platform-analysis/enums/subStep";
import { TableViewMode } from "@models/platform-analysis/enums/tableViewMode";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { ProductLOV } from "@models/product/entities/product";
import { MaximumProductInAppendixTable } from "@pages/platform-analysis-v2/_id/constants";
import { compileOverwritePortfolioValidationResult } from "@pages/platform-analysis/_id/steps/fee/calculator/components/portfolio-setup/utils";
import {
  buildHiddenIndexesMap,
  compileBusinessMetricAnalysisRows,
  compileBusinessMetricItemSectionState,
  compileFeatureAnalysisRows,
  compileFeeAnalysisSmaPortfolioRows,
  filterFeatureAnalysisRows,
  filterFeeAnalysisSelectedSubProducts,
  filterFeeAnalysisTableRows,
  splitFeatureAnalysis,
  updateReadOnlyAndComplete,
} from "@redux/slices/platform-analysis/util";
import { RootState } from "@redux/store";
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityState,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  getBusinessMetricSelectionStep,
  putBusinessMetricSelectionStep,
} from "@services/platform-analysis/bisinessMetricAnalysisService";
import {
  getFeatureSelectionStep,
  putFeatureSelectionStep,
} from "@services/platform-analysis/featureAnalysisService";
import {
  checkPortfolioAssignment,
  getFeeCalculatorStep,
  putFeeCalculatorStep,
} from "@services/platform-analysis/feeAnalysisService";
import {
  analysisDisqualifyAllPlatforms,
  checkAnalysisUniqueName,
  createPlatformAnalysisPortfolio,
  getAdviserUsers,
  getFamilyGroups,
  getFamilyMembers,
  getPlatformAnalysisAllSteps,
  getPlatformAnalysisBannerName,
  getPlatformAnalysisOwners,
  getPlatformAnalysisProducts,
  getPlatformAnalysisStep,
  postPlatformAnalysis,
  putPlatformAnalysis,
  putPlatformAnalysisCurrentStep,
  savePlatformAnalysisFilters,
  updateAnalysisDecision,
  updateAnalysisFinalJSONs,
  updatePlatformAnalysisPortfolio,
} from "@services/platform-analysis/platformAnalysisService";
import { isEmpty, isNil } from "lodash";
import { FieldPath } from "react-hook-form/dist/types";

const { Setup, Feature, BusinessMetric, Fee, Summary } = AnalysisStep;
const { Selection, Analysis } = AnalysisSubStep;
const stepEnums = [Setup, Feature, BusinessMetric, Fee, Summary];

const featureAnalysisTableRowsAdapter =
  createEntityAdapter<FeatureAnalysisRowDTO>({
    selectId: (row) => row.id,
  });
const businessMetricAnalysisTableRowsAdapter =
  createEntityAdapter<FeatureAnalysisRowDTO>({
    selectId: (row) => row.id || row.name,
  });
const feeAnalysisSmaTableRowsAdapter =
  createEntityAdapter<FeatureAnalysisRowDTO>({
    selectId: (row) => row.id || row.variableName || row.name,
  });
const summaryFeatureAnalysisShortlistedTableRowsAdapter =
  createEntityAdapter<FeatureAnalysisRowDTO>({
    selectId: (row) => row.id,
  });
const summaryFeatureAnalysisDisqualifiedTableRowsAdapter =
  createEntityAdapter<FeatureAnalysisRowDTO>({
    selectId: (row) => row.id,
  });
const summaryBusinessMetricAnalysisShortlistedTableRowsAdapter =
  createEntityAdapter<FeatureAnalysisRowDTO>({
    selectId: (row) => row.id || row.name,
  });
const summaryBusinessMetricAnalysisDisqualifiedTableRowsAdapter =
  createEntityAdapter<FeatureAnalysisRowDTO>({
    selectId: (row) => row.id || row.name,
  });

export interface PlatformAnalysisStore {
  ui: {
    isValid?: boolean;
    isDirty: boolean;
    isSubmitting?: boolean;
    isValidating?: boolean;
    isAccessDenied?: boolean;
    shouldShowSaveProgressWarning: boolean;
    isValidPortfolioSetups: boolean;
    isLoading?: boolean;
    isLoadingSteps?: boolean;
    isLoadingBanner?: boolean;
    /** @deprecated Property removed on merged PA/SR */
    isShowHidden?: boolean;
    discardedStages?: AnalysisStep[];
    selectedStep?: AnalysisStep;
    selectedSubStep?: AnalysisSubStep;
    selectedStepIndex?: number;
    selectedSubStepIndex?: number;
    steps: {
      [key in AnalysisStep]: {
        isHidden?: boolean;
        isUnHighlighted?: boolean;
        subSteps?: {
          [key in AnalysisSubStep]: {
            isHidden?: boolean;
          };
        };
      };
    };
    filterFeature: PlatformAnalysisFilter[];
    collapsedSubGroupIds?: string[];
    reachAnalysisSetupPage?: "FIRST_TIME" | "SECOND_TIME";
    totalCollapsedFeatureAnalysisRows: number;
    totalCollapsedBusinessMetricRows: number;
    isShowAnalysisScores: boolean;
    portfolioLevel?: FeeCalculatorPortfolioLevel;
    featureAnalysis: {
      tableRows: EntityState<FeatureAnalysisRowDTO>;
      initialTableRows: EntityState<FeatureAnalysisRowDTO>;
    };
    businessMetricAnalysis: {
      tableRows: EntityState<FeatureAnalysisRowDTO>;
      initialTableRows: EntityState<FeatureAnalysisRowDTO>;
    };
    feeAnalysis: {
      smaTableRows: EntityState<FeatureAnalysisRowDTO>;
      disqualifiedSmaTableRows: EntityState<FeatureAnalysisRowDTO>;
      smaInitialTableRows: EntityState<FeatureAnalysisRowDTO>;
      disqualifiedSmaInitialTableRows: EntityState<FeatureAnalysisRowDTO>;
    };
    summary: {
      featureAnalysis: {
        shortlistedTableRows: EntityState<FeatureAnalysisRowDTO>;
        disqualifiedTables: EntityState<FeatureAnalysisRowDTO>[];
        disqualifiedProductChunk: SelectedProductDTO[][];
      };
      businessMetricAnalysis: {
        shortlistedTableRows: EntityState<FeatureAnalysisRowDTO>;
        disqualifiedTables: EntityState<FeatureAnalysisRowDTO>[];
        disqualifiedProductChunk: SelectedProductDTO[][];
      };
    };
  };
  isCompleted?: boolean;
  isReadOnly?: boolean;
  analysisSteps?: AnalysisStepDTO;
  platformAnalysis?: AnalysisDTO;
  bannerName?: AnalysisBannerNameDTO;
  featureSelections?: AnalysisGetFeatureSectionsDTO;
  originalFeatureSelections?: AnalysisGetFeatureSectionsDTO;
  businessMetricSelections?: BusinessMetricCategories;
  feeCalculator?: AnalysisFeeCalculatorDTO;
  disclaimers?: DisclaimerDTO[];
  lineChartColors?: { [key in string]: string };
  analysisPlatforms?: ProductLOV[];

  internal: {
    featureAnalysis: {
      collapseFilterLevel: FeatureAnalysisCollapseStateFilterLevel;
      essentialFilterLevel: FeatureAnalysisEssentialFilterLevel;
      availabilityFilterLevel: FeatureAnalysisAvailabilityFilterLevel;
      collapseStateChangedManually: boolean;
    };
    businessMetricSelection: {
      initialItemSelectionState: Record<string, boolean>;
      currentItemSelectionState: Record<string, boolean>;
    };
    feeCalculator: {
      feeCalculatorPathsMap: Partial<
        Record<
          AnalysisVariable,
          {
            feeCalculatorPath: FieldPath<AnalysisFeeCalculatorDTO>;
            dataValuePath: FieldPath<AnalysisFeeCalculatorDTO>;
            databaseName?: string;
          }
        >
      >;
      sectionsHaveError: Partial<Record<FeeCalculatorSectionId, boolean>>;
      validatedPortfolioSetupFirstTime?: boolean;
    };
    feeAnalysis: {
      filterInvestmentMenuValue?: FeeInvestmentMenuOption;
      filterMissingInvestmentValue?: FeeMissingInvestmentsOption;
      disqualifiedFilterInvestmentMenuValue?: FeeInvestmentMenuOption;
      disqualifiedFilterMissingInvestmentValue?: FeeMissingInvestmentsOption;
      hiddenIds: string[];
      disqualifiedHiddenIds: string[];
      heatmapMode: FeeHeatmapMode;
      displayStyle: FeesDisplayStyle;
      displayMode: FeesDisplayMode;
      performanceScope: FeesPerformanceScope;
      hiddenIdsIndexMap?: boolean[];
      disqualifiedHiddenIdsIndexMap?: boolean[];
      subProductFiltered: FeeSubProductDTO[];
      disqualifiedSubProductFiltered: FeeSubProductDTO[];
    };
    summary: {
      productRecommendations: Record<string, Partial<ProductRecommendationDTO>>;
      featureAnalysisTableViewMode: TableViewMode;
    };
  };
}

const initialState: PlatformAnalysisStore = {
  ui: {
    isValid: true,
    isDirty: false,
    isSubmitting: false,
    isValidating: false,
    isAccessDenied: undefined,
    isShowHidden: false,
    shouldShowSaveProgressWarning: false,
    isValidPortfolioSetups: false,
    discardedStages: [],
    selectedStepIndex: 0,
    selectedSubStepIndex: 0,
    steps: {
      [Setup]: {
        isHidden: false,
        isUnHighlighted: false,
      },
      [Feature]: {
        isHidden: true,
        isUnHighlighted: true,
        subSteps: {
          [Selection]: {
            isHidden: false,
          },
          [Analysis]: {
            isHidden: false,
          },
        },
      },
      [BusinessMetric]: {
        isHidden: true,
        isUnHighlighted: true,
        subSteps: {
          [Selection]: {
            isHidden: false,
          },
          [Analysis]: {
            isHidden: false,
          },
        },
      },
      [Fee]: {
        isHidden: true,
        isUnHighlighted: true,
        subSteps: {
          [Selection]: {
            isHidden: false,
          },
          [Analysis]: {
            isHidden: false,
          },
        },
      },
      [Summary]: {
        isHidden: false,
        isUnHighlighted: true,
      },
    },
    filterFeature: [
      PlatformAnalysisFilter.Common,
      PlatformAnalysisFilter.Differentiators,
    ],
    collapsedSubGroupIds: [],
    reachAnalysisSetupPage: undefined,
    totalCollapsedFeatureAnalysisRows: 0,
    totalCollapsedBusinessMetricRows: 0,
    isShowAnalysisScores: true,
    featureAnalysis: {
      tableRows: featureAnalysisTableRowsAdapter.getInitialState(),
      initialTableRows: featureAnalysisTableRowsAdapter.getInitialState(),
    },
    businessMetricAnalysis: {
      tableRows: businessMetricAnalysisTableRowsAdapter.getInitialState(),
      initialTableRows:
        businessMetricAnalysisTableRowsAdapter.getInitialState(),
    },
    feeAnalysis: {
      smaTableRows: feeAnalysisSmaTableRowsAdapter.getInitialState(),
      disqualifiedSmaTableRows:
        feeAnalysisSmaTableRowsAdapter.getInitialState(),
      smaInitialTableRows: feeAnalysisSmaTableRowsAdapter.getInitialState(),
      disqualifiedSmaInitialTableRows:
        feeAnalysisSmaTableRowsAdapter.getInitialState(),
    },
    summary: {
      featureAnalysis: {
        shortlistedTableRows:
          summaryFeatureAnalysisShortlistedTableRowsAdapter.getInitialState(),
        disqualifiedTables: [],
        disqualifiedProductChunk: [],
      },
      businessMetricAnalysis: {
        shortlistedTableRows:
          summaryBusinessMetricAnalysisShortlistedTableRowsAdapter.getInitialState(),
        disqualifiedTables: [],
        disqualifiedProductChunk: [],
      },
    },
    portfolioLevel: undefined,
  },
  isCompleted: false,
  isReadOnly: false,
  lineChartColors: undefined,
  analysisPlatforms: [],
  internal: {
    featureAnalysis: {
      collapseFilterLevel: FeatureAnalysisCollapseStateFilterLevel.FeatureLevel,
      essentialFilterLevel: FeatureAnalysisEssentialFilterLevel.All,
      availabilityFilterLevel: FeatureAnalysisAvailabilityFilterLevel.All,
      collapseStateChangedManually: false,
    },
    businessMetricSelection: {
      initialItemSelectionState: {},
      currentItemSelectionState: {},
    },
    feeCalculator: {
      feeCalculatorPathsMap: {},
      sectionsHaveError: {},
      validatedPortfolioSetupFirstTime: false,
    },
    feeAnalysis: {
      subProductFiltered: [],
      disqualifiedSubProductFiltered: [],
      hiddenIds: [],
      disqualifiedHiddenIds: [],
      heatmapMode: FeeHeatmapMode.Detail,
      displayStyle: FeesDisplayStyle.Dollar,
      performanceScope: FeesPerformanceScope.Including,
      displayMode: FeesDisplayMode.Detail,
      hiddenIdsIndexMap: undefined,
      disqualifiedHiddenIdsIndexMap: undefined,
    },
    summary: {
      productRecommendations: {},
      featureAnalysisTableViewMode: TableViewMode.Default,
    },
  },
};

const platformAnalysisSlice = createSlice({
  name: "platformAnalysis",
  initialState,
  reducers: {
    updateSelectedStepIndex: (
      state,
      action: PayloadAction<number | undefined>,
    ) => {
      state.ui.selectedStepIndex = action.payload;
    },
    updateSelectedSubStepIndex: (
      state,
      action: PayloadAction<number | undefined>,
    ) => {
      state.ui.selectedSubStepIndex = action.payload;
    },
    updateIsValid: (state, action: PayloadAction<boolean | undefined>) => {
      state.ui.isValid = action.payload;
    },
    updateIsCompleted: (state, action: PayloadAction<boolean | undefined>) => {
      state.isCompleted = action.payload;
    },
    updateIsReadOnly: (state, action: PayloadAction<boolean | undefined>) => {
      state.isReadOnly = action.payload;
    },
    updateIsDirty: (state, action: PayloadAction<boolean>) => {
      state.ui.isDirty = action.payload;
    },
    updateIsShowHidden: (state, action: PayloadAction<boolean | undefined>) => {
      state.ui.isShowHidden = action.payload;
    },
    updateIsShowAnalysisScores: (state, action: PayloadAction<boolean>) => {
      state.ui.isShowAnalysisScores = action.payload;
    },
    updateIsAccessDenied: (
      state,
      action: PayloadAction<boolean | undefined>,
    ) => {
      state.ui.isAccessDenied = action.payload;
    },
    updateShouldShowSaveProgressWarning: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.ui.shouldShowSaveProgressWarning = action.payload;
    },
    updateIsValidPortfolioSetups: (state, action: PayloadAction<boolean>) => {
      state.ui.isValidPortfolioSetups = action.payload;
    },
    updateAnalysisSteps: (
      state,
      action: PayloadAction<AnalysisStepDTO | undefined>,
    ) => {
      state.analysisSteps = action.payload;
    },
    updateStepHidden: (
      state,
      action: PayloadAction<{
        step: AnalysisStep;
        isHidden: boolean | undefined;
      }>,
    ) => {
      state.ui.steps[action.payload.step].isHidden = action.payload.isHidden;
    },
    updateStepUnHighlighted: (
      state,
      action: PayloadAction<{
        step: AnalysisStep;
        isUnHighlighted: boolean | undefined;
      }>,
    ) => {
      state.ui.steps[action.payload.step].isUnHighlighted =
        action.payload.isUnHighlighted;
    },
    updateBannerName: (state, action: PayloadAction<AnalysisBannerNameDTO>) => {
      state.bannerName = { ...state.bannerName, ...action.payload };
    },
    updatePlatformAnalysisStore: (
      state,
      action: PayloadAction<AnalysisDTO | undefined>,
    ) => {
      state.platformAnalysis = action.payload;
    },
    updateFeatureSelectionsGroup: (
      state,
      action: PayloadAction<AnalysisConfigurationFeatureSectionDTO[]>,
    ) => {
      if (state.featureSelections?.sections) {
        state.featureSelections.sections = action.payload;
      }
    },
    updateFeatureSelectionStore: (
      state,
      action: PayloadAction<AnalysisGetFeatureSectionsDTO | undefined>,
    ) => {
      state.featureSelections = action.payload;
    },
    updateBusinessMetricSelectionStore: (
      state,
      action: PayloadAction<BusinessMetricCategories | undefined>,
    ) => {
      state.businessMetricSelections = action.payload;
    },
    updateBusinessMetricItemSelectionStore: (
      state,
      action: PayloadAction<{
        id?: string;
        isSelected: boolean;
        category: keyof BusinessMetricCategories;
      }>,
    ) => {
      if (!state.businessMetricSelections) return;

      const { id, isSelected, category } = action.payload;
      const categoryItems = state.businessMetricSelections[category];

      if (!categoryItems || !Array.isArray(categoryItems)) return;

      const { initialItemSelectionState, currentItemSelectionState } =
        state.internal.businessMetricSelection;

      state.businessMetricSelections = {
        ...state.businessMetricSelections,
        [category]: categoryItems.map((item) => {
          if (isNil(id) || item.id === id) {
            currentItemSelectionState[item.id] = isSelected;

            return { ...item, isSelected };
          }

          return item;
        }),
      };

      state.ui.isDirty = Object.keys(initialItemSelectionState).some(
        (key) =>
          initialItemSelectionState[key] !== currentItemSelectionState[key],
      );
      state.ui.isValid = Object.values(currentItemSelectionState).some(
        (value) => value,
      );
    },
    updateFeeCalculatorStore: (
      state,
      action: PayloadAction<AnalysisFeeCalculatorDTO | undefined>,
    ) => {
      state.feeCalculator = action.payload;
    },
    resetAnalysisStore: (state) => {
      state.analysisSteps = undefined;
      state.bannerName = undefined;
      state.platformAnalysis = undefined;
      state.featureSelections = undefined;
      state.businessMetricSelections = undefined;
      state.feeCalculator = undefined;
      state.isCompleted = false;
      state.isReadOnly = false;
      state.ui = initialState.ui;
    },
    updateFilterFeature: (
      state,
      action: PayloadAction<PlatformAnalysisFilter[]>,
    ) => {
      state.ui.filterFeature = action.payload;
    },
    updateCollapsedSubGroupIds: (state, action: PayloadAction<string[]>) => {
      state.ui.collapsedSubGroupIds = action.payload;
    },
    updateReachAnalysisSetupPage: (
      state,
      action: PayloadAction<"FIRST_TIME" | "SECOND_TIME" | undefined>,
    ) => {
      state.ui.reachAnalysisSetupPage = action.payload;
    },
    updateDisclaimers: (
      state,
      action: PayloadAction<DisclaimerDTO[] | undefined>,
    ) => {
      state.disclaimers = action.payload;
    },
    updateTotalCollapsedFeatureAnalysisRowsAction: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.ui.totalCollapsedFeatureAnalysisRows += action.payload;
    },
    updateTotalCollapsedBusinessMetricRowsAction: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.ui.totalCollapsedBusinessMetricRows += action.payload;
    },
    updateLineChartColorsAction: (
      state,
      action: PayloadAction<{ [key in string]: string }>,
    ) => {
      state.lineChartColors = action.payload;
    },
    resetFee: (state) => {
      if (state.platformAnalysis) {
        state.platformAnalysis.fee = null;
      }
    },
    setDiscardedStages: (state, action: PayloadAction<AnalysisStep[]>) => {
      state.ui.discardedStages = action.payload;
    },
    setFeatureAnalysisRowFilters: (
      state,
      action: PayloadAction<{
        collapseLevel?: FeatureAnalysisCollapseStateFilterLevel;
        essentialLevel?: FeatureAnalysisEssentialFilterLevel;
        availabilityLevel?: FeatureAnalysisAvailabilityFilterLevel;
        resetCollapseStateChangedManually?: boolean;
      }>,
    ) => {
      const {
        resetCollapseStateChangedManually,
        collapseLevel:
          collapseFilterLevel = FeatureAnalysisCollapseStateFilterLevel.FeatureLevel,
        essentialLevel:
          essentialFilterLevel = FeatureAnalysisEssentialFilterLevel.All,
        availabilityLevel:
          availabilityFilterLevel = FeatureAnalysisAvailabilityFilterLevel.All,
      } = action.payload;

      const initialRows = featureAnalysisTableRowsAdapter
        .getSelectors()
        .selectAll(state.ui.featureAnalysis.initialTableRows);

      const newFilteredRows = filterFeatureAnalysisRows({
        rows: initialRows,
        essentialFilterLevel,
        availabilityFilterLevel,
      });

      featureAnalysisTableRowsAdapter.setAll(
        state.ui.featureAnalysis.tableRows,
        newFilteredRows,
      );

      state.internal.featureAnalysis.essentialFilterLevel =
        essentialFilterLevel;

      state.internal.featureAnalysis.availabilityFilterLevel =
        availabilityFilterLevel;

      const isCollapseStateChangedManually =
        state.internal.featureAnalysis.collapseStateChangedManually;
      if (
        !isCollapseStateChangedManually ||
        resetCollapseStateChangedManually
      ) {
        state.internal.featureAnalysis.collapseFilterLevel =
          collapseFilterLevel;
      }

      if (!resetCollapseStateChangedManually) return;
      state.internal.featureAnalysis.collapseStateChangedManually = false;
    },
    updateFeeCalculatorSectionErrorState: (
      state,
      action: PayloadAction<{ key: FeeCalculatorSectionId; hasError: boolean }>,
    ) => {
      const { key, hasError } = action.payload;
      if (!key) return;
      state.internal.feeCalculator.sectionsHaveError[key] = hasError;
    },
    setPortfolioLevel: (
      state,
      action: PayloadAction<FeeCalculatorPortfolioLevel>,
    ) => {
      state.ui.portfolioLevel = action.payload;
    },
    setFeeAnalysisFilters: (
      state,
      action: PayloadAction<{
        investmentMenuValue?: FeeInvestmentMenuOption;
        missingInvestmentValue?: FeeMissingInvestmentsOption;
      }>,
    ) => {
      const isSummaryStep =
        state.platformAnalysis?.currentStep === AnalysisStep.Summary;
      const allSubProducts = isSummaryStep
        ? state.platformAnalysis?.summary?.feeAnalysis?.shortlisted?.subProducts
        : state.platformAnalysis?.fee?.all.subProducts;

      if (!allSubProducts) return;

      const investmentMenuFilter = action.payload.investmentMenuValue;
      const missingInvestmentFilter = action.payload.missingInvestmentValue;

      const subProductFiltered = filterFeeAnalysisSelectedSubProducts(
        allSubProducts,
        investmentMenuFilter ?? FeeInvestmentMenuOption.AllPlatform,
        missingInvestmentFilter ?? FeeMissingInvestmentsOption.AllPlatform,
      );

      state.internal.feeAnalysis.filterInvestmentMenuValue =
        investmentMenuFilter;
      state.internal.feeAnalysis.filterMissingInvestmentValue =
        missingInvestmentFilter;
      state.internal.feeAnalysis.subProductFiltered = subProductFiltered;
      state.internal.feeAnalysis.hiddenIdsIndexMap = buildHiddenIndexesMap(
        allSubProducts,
        subProductFiltered,
      );
    },
    setSummaryFeeAnalysisFilters: (
      state,
      action: PayloadAction<{
        investmentMenuValue?: FeeInvestmentMenuOption;
        missingInvestmentValue?: FeeMissingInvestmentsOption;
        sortedSubProducts: FeeSubProductDTO[];
      }>,
    ) => {
      const { investmentMenuValue, missingInvestmentValue, sortedSubProducts } =
        action.payload;

      const subProductFiltered = filterFeeAnalysisSelectedSubProducts(
        sortedSubProducts,
        investmentMenuValue ?? FeeInvestmentMenuOption.AllPlatform,
        missingInvestmentValue ?? FeeMissingInvestmentsOption.AllPlatform,
      );

      state.internal.feeAnalysis.filterInvestmentMenuValue =
        investmentMenuValue;
      state.internal.feeAnalysis.filterMissingInvestmentValue =
        missingInvestmentValue;
      state.internal.feeAnalysis.subProductFiltered = subProductFiltered;
      state.internal.feeAnalysis.hiddenIdsIndexMap = buildHiddenIndexesMap(
        sortedSubProducts,
        subProductFiltered,
      );
    },
    setFeeAnalysisDisqualifiedFilters: (
      state,
      action: PayloadAction<{
        investmentMenuValue?: FeeInvestmentMenuOption;
        missingInvestmentValue?: FeeMissingInvestmentsOption;
      }>,
    ) => {
      const allSubProducts =
        state.platformAnalysis?.summary?.feeAnalysis?.disqualified?.subProducts;

      if (!allSubProducts) return;

      const investmentMenuFilter = action.payload.investmentMenuValue;
      const missingInvestmentFilter = action.payload.missingInvestmentValue;

      const subProductFiltered = filterFeeAnalysisSelectedSubProducts(
        allSubProducts,
        investmentMenuFilter ?? FeeInvestmentMenuOption.AllPlatform,
        missingInvestmentFilter ?? FeeMissingInvestmentsOption.AllPlatform,
      );

      state.internal.feeAnalysis.disqualifiedFilterInvestmentMenuValue =
        investmentMenuFilter;
      state.internal.feeAnalysis.disqualifiedFilterMissingInvestmentValue =
        missingInvestmentFilter;
      state.internal.feeAnalysis.disqualifiedSubProductFiltered =
        subProductFiltered;
      state.internal.feeAnalysis.disqualifiedHiddenIdsIndexMap =
        buildHiddenIndexesMap(allSubProducts, subProductFiltered);
    },
    setFeeAnalysisDisqualifiedHiddenIds: (
      state,
      action: PayloadAction<string[]>,
    ) => {
      state.internal.feeAnalysis.disqualifiedHiddenIds = action.payload;

      const allSubProducts =
        state.platformAnalysis?.summary?.feeAnalysis?.disqualified?.subProducts;

      if (!allSubProducts) return;

      const investmentMenuFilter =
        state.internal.feeAnalysis.disqualifiedFilterInvestmentMenuValue;
      const missingInvestmentFilter =
        state.internal.feeAnalysis.disqualifiedFilterMissingInvestmentValue;

      const subProductFiltered = filterFeeAnalysisSelectedSubProducts(
        allSubProducts,
        investmentMenuFilter ?? FeeInvestmentMenuOption.AllPlatform,
        missingInvestmentFilter ?? FeeMissingInvestmentsOption.AllPlatform,
      );

      if (!isEmpty(action.payload)) {
        const filteredIds = new Set(subProductFiltered.map((item) => item.id));
        const hiddenIds = new Set(action.payload);
        const hiddenIndexes =
          state.internal.feeAnalysis.disqualifiedHiddenIdsIndexMap ?? [];

        allSubProducts.forEach((subProduct, index) => {
          if (filteredIds.has(subProduct.id)) {
            hiddenIndexes[index] = false;
          }

          if (hiddenIds.has(subProduct.id)) {
            hiddenIndexes[index] = true;
          }
        });

        state.internal.feeAnalysis.disqualifiedHiddenIdsIndexMap =
          hiddenIndexes;

        return;
      }

      state.internal.feeAnalysis.disqualifiedHiddenIdsIndexMap =
        buildHiddenIndexesMap(allSubProducts, subProductFiltered);
    },
    setFeeAnalysisHiddenIds: (state, action: PayloadAction<string[]>) => {
      state.internal.feeAnalysis.hiddenIds = action.payload;

      const allSubProducts = state.platformAnalysis?.fee?.all.subProducts;

      if (!allSubProducts) return;

      const investmentMenuFilter =
        state.internal.feeAnalysis.filterInvestmentMenuValue;
      const missingInvestmentFilter =
        state.internal.feeAnalysis.filterMissingInvestmentValue;

      const subProductFiltered = filterFeeAnalysisSelectedSubProducts(
        allSubProducts,
        investmentMenuFilter ?? FeeInvestmentMenuOption.AllPlatform,
        missingInvestmentFilter ?? FeeMissingInvestmentsOption.AllPlatform,
      );

      if (!isEmpty(action.payload)) {
        const filteredIds = new Set(subProductFiltered.map((item) => item.id));
        const hiddenIds = new Set(action.payload);
        const hiddenIndexes =
          state.internal.feeAnalysis.hiddenIdsIndexMap ?? [];

        allSubProducts.forEach((subProduct, index) => {
          if (filteredIds.has(subProduct.id)) {
            hiddenIndexes[index] = false;
          }

          if (hiddenIds.has(subProduct.id)) {
            hiddenIndexes[index] = true;
          }
        });

        state.internal.feeAnalysis.hiddenIdsIndexMap = hiddenIndexes;

        return;
      }

      state.internal.feeAnalysis.hiddenIdsIndexMap = buildHiddenIndexesMap(
        allSubProducts,
        subProductFiltered,
      );
    },
    setSummaryFeeAnalysisHiddenIds: (
      state,
      action: PayloadAction<{
        hiddenIds: string[];
        sortedSubProducts?: FeeSubProductDTO[];
      }>,
    ) => {
      const { hiddenIds, sortedSubProducts } = action.payload;

      state.internal.feeAnalysis.hiddenIds = hiddenIds;

      const investmentMenuFilter =
        state.internal.feeAnalysis.filterInvestmentMenuValue;
      const missingInvestmentFilter =
        state.internal.feeAnalysis.filterMissingInvestmentValue;

      const subProductFiltered = filterFeeAnalysisSelectedSubProducts(
        sortedSubProducts ?? [],
        investmentMenuFilter ?? FeeInvestmentMenuOption.AllPlatform,
        missingInvestmentFilter ?? FeeMissingInvestmentsOption.AllPlatform,
      );

      if (isEmpty(hiddenIds)) {
        state.internal.feeAnalysis.hiddenIdsIndexMap = buildHiddenIndexesMap(
          sortedSubProducts ?? [],
          subProductFiltered,
        );

        return;
      }

      const newHiddenIndexes: boolean[] = [];
      const filteredIds = subProductFiltered.map((item) => item.id);

      sortedSubProducts?.forEach((subProduct) => {
        const isHidden =
          hiddenIds.includes(subProduct.id) ||
          !filteredIds.includes(subProduct.id);

        newHiddenIndexes.push(isHidden);
      });

      state.internal.feeAnalysis.hiddenIdsIndexMap = newHiddenIndexes;
    },
    setFeeAnalysisHeatmapMode: (
      state,
      action: PayloadAction<FeeHeatmapMode>,
    ) => {
      state.internal.feeAnalysis.heatmapMode = action.payload;
    },
    setFeeAnalysisDisplayStyle: (
      state,
      action: PayloadAction<FeesDisplayStyle>,
    ) => {
      state.internal.feeAnalysis.displayStyle = action.payload;
    },
    setFeeAnalysisPerformanceScope: (
      state,
      action: PayloadAction<FeesPerformanceScope>,
    ) => {
      const scope = action.payload;
      state.internal.feeAnalysis.performanceScope = scope;

      const initRows = feeAnalysisSmaTableRowsAdapter
        .getSelectors()
        .selectAll(state.ui.feeAnalysis.smaInitialTableRows);
      const filteredRows = filterFeeAnalysisTableRows({
        rows: initRows,
        performanceScope: scope,
        displayMode: state.internal.feeAnalysis.displayMode,
      });

      feeAnalysisSmaTableRowsAdapter.setAll(
        state.ui.feeAnalysis.smaTableRows,
        filteredRows,
      );

      const disqualifiedInitRows = feeAnalysisSmaTableRowsAdapter
        .getSelectors()
        .selectAll(state.ui.feeAnalysis.disqualifiedSmaInitialTableRows);
      const disqualifiedFilteredRows = filterFeeAnalysisTableRows({
        rows: disqualifiedInitRows,
        performanceScope: scope,
        displayMode: state.internal.feeAnalysis.displayMode,
      });
      feeAnalysisSmaTableRowsAdapter.setAll(
        state.ui.feeAnalysis.disqualifiedSmaTableRows,
        disqualifiedFilteredRows,
      );
    },
    setFeeAnalysisDisplayMode: (
      state,
      action: PayloadAction<FeesDisplayMode>,
    ) => {
      state.internal.feeAnalysis.displayMode = action.payload;

      const initRows = feeAnalysisSmaTableRowsAdapter
        .getSelectors()
        .selectAll(state.ui.feeAnalysis.smaInitialTableRows);
      const filteredRows = filterFeeAnalysisTableRows({
        rows: initRows,
        performanceScope: state.internal.feeAnalysis.performanceScope,
        displayMode: action.payload,
      });

      feeAnalysisSmaTableRowsAdapter.setAll(
        state.ui.feeAnalysis.smaTableRows,
        filteredRows,
      );

      const disqualifiedInitRows = feeAnalysisSmaTableRowsAdapter
        .getSelectors()
        .selectAll(state.ui.feeAnalysis.disqualifiedSmaInitialTableRows);
      const disqualifiedFilteredRows = filterFeeAnalysisTableRows({
        rows: disqualifiedInitRows,
        performanceScope: state.internal.feeAnalysis.performanceScope,
        displayMode: action.payload,
      });
      feeAnalysisSmaTableRowsAdapter.setAll(
        state.ui.feeAnalysis.disqualifiedSmaTableRows,
        disqualifiedFilteredRows,
      );
    },
    setValidatedPortfolioSetupFirstTime: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.internal.feeCalculator.validatedPortfolioSetupFirstTime =
        action.payload;
    },
    setCollapseStateChangedManually: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.internal.featureAnalysis.collapseStateChangedManually =
        action.payload;
    },
    setProductRecommendations: (
      state,
      action: PayloadAction<{
        productRecommendations: Record<
          string,
          Partial<ProductRecommendationDTO>
        >;
        sortedSubProducts?: FeeSubProductDTO[];
      }>,
    ) => {
      const { productRecommendations, sortedSubProducts } = action.payload;

      state.internal.summary.productRecommendations = productRecommendations;

      if (!sortedSubProducts) return;

      const currentHiddenIds = state.internal.feeAnalysis.hiddenIds;

      const filteredSubProductIds = filterFeeAnalysisSelectedSubProducts(
        sortedSubProducts,
        state.internal.feeAnalysis.filterInvestmentMenuValue ??
          FeeInvestmentMenuOption.AllPlatform,
        state.internal.feeAnalysis.filterMissingInvestmentValue ??
          FeeMissingInvestmentsOption.AllPlatform,
      )?.map(({ id }) => id);

      state.internal.feeAnalysis.hiddenIdsIndexMap = sortedSubProducts?.map(
        ({ id }) =>
          currentHiddenIds.includes(id) || !filteredSubProductIds.includes(id),
      );
    },
    setSingleProductRecommendation: (
      state,
      action: PayloadAction<{
        productId: string;
        recommendation: Partial<ProductRecommendationDTO>;
      }>,
    ) => {
      const { productId, recommendation } = action.payload;
      state.internal.summary.productRecommendations[productId] = recommendation;
    },
    setFeatureAnalysisTableViewMode: (
      state,
      action: PayloadAction<TableViewMode>,
    ) => {
      state.internal.summary.featureAnalysisTableViewMode = action.payload;
    },
  },
  extraReducers(builder) {
    //createPlatformAnalysis
    builder.addCase(createPlatformAnalysisThunk.pending, (state) => {
      state.ui.isSubmitting = true;
    });
    builder.addCase(createPlatformAnalysisThunk.fulfilled, (state, action) => {
      state.platformAnalysis = action.payload.isSuccess
        ? action.payload?.data
        : undefined;
      state.ui.isSubmitting = false;
    });
    builder.addCase(createPlatformAnalysisThunk.rejected, (state) => {
      state.ui.isSubmitting = false;
    });

    //updatePlatformAnalysis
    builder.addCase(updatePlatformAnalysisThunk.pending, (state) => {
      state.ui.isSubmitting = true;
    });
    builder.addCase(updatePlatformAnalysisThunk.fulfilled, (state) => {
      state.ui.isSubmitting = false;
    });
    builder.addCase(updatePlatformAnalysisThunk.rejected, (state) => {
      state.ui.isSubmitting = false;
    });

    //getPlatformAnalysis
    builder.addCase(getPlatformAnalysisThunk.pending, (state) => {
      state.ui.isLoading = true;
    });
    builder.addCase(getPlatformAnalysisThunk.fulfilled, (state, action) => {
      const payloadData = action.payload?.data;
      state.platformAnalysis = action.payload.isSuccess
        ? payloadData
        : undefined;
      //updateReadOnlyAndComplete
      const newState = updateReadOnlyAndComplete(state, {
        isCompleted: state.platformAnalysis?.isCompleted,
        status: state.platformAnalysis?.status,
      });
      state.ui = newState.ui;
      state.ui.isShowAnalysisScores =
        !!state.platformAnalysis?.isShowAnalysisScore;
      state.isCompleted = newState.isCompleted;
      state.isReadOnly = newState.isReadOnly;

      const { stepId } = action.meta.arg;
      state.ui.selectedStep = stepId;

      // Prepare feature analysis data
      if (stepId === Feature) {
        prepareFeatureAnalysisData({ state });
      }

      // Prepare business metric analysis data
      if (stepId === BusinessMetric) {
        prepareBusinessMetricAnalysisData({ state });
        state.ui.isShowAnalysisScores = true;
      }

      // Prepare fee analysis data
      if (stepId === Fee) {
        prepareFeeAnalysisData({ state, payloadData });
      }

      // Prepare summary data
      if (stepId === Summary) {
        const hasFeatureStep = payloadData?.summary?.hasFeatureStep;
        const hasBusinessMetricStep =
          payloadData?.summary?.hasBusinessMetricStep;
        const hasFeeStep = payloadData?.summary?.hasFeeStep;

        hasFeatureStep &&
          prepareFeatureAnalysisData({ state, isSummary: true });
        hasBusinessMetricStep &&
          prepareBusinessMetricAnalysisData({ state, isSummary: true });
        hasFeeStep &&
          prepareFeeAnalysisData({
            state,
            payloadData: payloadData,
            isSummary: true,
          });
        prepareProductOrdersData({ state });
      }

      //Set isLoading
      state.ui.isLoading = false;
    });
    builder.addCase(getPlatformAnalysisThunk.rejected, (state) => {
      state.ui.isLoading = false;
    });

    //getAnalysisBannerName
    builder.addCase(getAnalysisBannerNameThunk.pending, (state) => {
      state.ui.isLoadingBanner = true;
    });
    builder.addCase(getAnalysisBannerNameThunk.fulfilled, (state, action) => {
      state.bannerName = action.payload.isSuccess
        ? action.payload?.data
        : undefined;
      state.ui.isLoadingBanner = false;
    });
    builder.addCase(getAnalysisBannerNameThunk.rejected, (state) => {
      state.ui.isLoadingBanner = false;
    });

    //getPlatformAnalysisSteps
    builder.addCase(getPlatformAnalysisStepsThunk.pending, (state) => {
      state.ui.isLoadingSteps = true;
    });
    builder.addCase(
      getPlatformAnalysisStepsThunk.fulfilled,
      (state, action) => {
        if (action.payload.isSuccess && action.payload.data) {
          const { steps, currentStep, currentSubStep, savedSteps } =
            action.payload.data;

          let selectedStepIndex = -1;
          stepEnums.forEach((step) => {
            if (steps.includes(step)) {
              state.ui.steps[step].isHidden = false;
              selectedStepIndex++;
            } else {
              state.ui.steps[step].isHidden = true;
            }
            if (currentStep === step) {
              state.ui.selectedStepIndex = selectedStepIndex;
            }
            state.ui.steps[step].isUnHighlighted = !savedSteps?.includes(step);
          });

          state.ui.selectedSubStepIndex =
            currentSubStep === AnalysisSubStep.Analysis ? 1 : 0;
        }
        state.ui.isLoadingSteps = false;
        state.analysisSteps = action.payload.data;
      },
    );
    builder.addCase(getPlatformAnalysisStepsThunk.rejected, (state) => {
      state.ui.isLoadingSteps = false;
    });

    // getAnalysisFeatureSelections;
    builder.addCase(getFeatureSelectionsThunk.pending, (state) => {
      state.ui.isLoading = true;
    });
    builder.addCase(getFeatureSelectionsThunk.fulfilled, (state, action) => {
      state.featureSelections = action.payload.isSuccess
        ? action.payload?.data
        : undefined;
      state.originalFeatureSelections = state.featureSelections;
      //updateReadOnlyAndComplete
      const newState = updateReadOnlyAndComplete(state, {
        isCompleted: action.payload?.data?.isCompleted,
        status: action.payload?.data?.status,
      });
      state.ui = newState.ui;
      state.isCompleted = newState.isCompleted;
      state.isReadOnly = newState.isReadOnly;
      state.ui.isLoading = false;
    });
    builder.addCase(getFeatureSelectionsThunk.rejected, (state) => {
      state.ui.isLoading = false;
    });

    //updateAnalysisFeatureSelections;
    builder.addCase(updateFeatureSelectionThunk.pending, (state) => {
      state.ui.isSubmitting = true;
    });
    builder.addCase(updateFeatureSelectionThunk.fulfilled, (state) => {
      state.originalFeatureSelections = state.featureSelections;
      state.ui.isSubmitting = false;
    });
    builder.addCase(updateFeatureSelectionThunk.rejected, (state) => {
      state.ui.isSubmitting = false;
    });

    // getAnalysisBusinessMetricSelections;
    builder.addCase(getBusinessMetricSelectionsThunk.pending, (state) => {
      state.ui.isLoading = true;
    });
    builder.addCase(
      getBusinessMetricSelectionsThunk.fulfilled,
      (state, action) => {
        state.businessMetricSelections = action.payload.isSuccess
          ? action.payload?.data
          : undefined;

        const itemSelectionState = compileBusinessMetricItemSectionState(
          state.businessMetricSelections,
        );
        state.internal.businessMetricSelection.initialItemSelectionState =
          state.internal.businessMetricSelection.currentItemSelectionState =
            itemSelectionState;
        state.ui.isValid = Object.values(itemSelectionState).some(
          (value) => value,
        );

        //updateReadOnlyAndComplete
        const newState = updateReadOnlyAndComplete(state, {
          isCompleted: action.payload?.data?.isCompleted,
          status: action.payload?.data?.status,
        });
        state.ui = newState.ui;
        state.isCompleted = newState.isCompleted;
        state.isReadOnly = newState.isReadOnly;
        state.ui.isLoading = false;
      },
    );
    builder.addCase(getBusinessMetricSelectionsThunk.rejected, (state) => {
      state.ui.isLoading = false;
    });

    //updateAnalysisBusinessMetricSelections;
    builder.addCase(updateBusinessMetricSelectionThunk.pending, (state) => {
      state.ui.isSubmitting = true;
    });
    builder.addCase(updateBusinessMetricSelectionThunk.fulfilled, (state) => {
      state.ui.isSubmitting = false;
      state.internal.businessMetricSelection.initialItemSelectionState =
        state.internal.businessMetricSelection.currentItemSelectionState =
          compileBusinessMetricItemSectionState(state.businessMetricSelections);
    });
    builder.addCase(updateBusinessMetricSelectionThunk.rejected, (state) => {
      state.ui.isSubmitting = false;
    });

    // getAnalysisFeeCalculator;
    builder.addCase(getFeeCalculatorThunk.pending, (state) => {
      state.ui.isLoading = true;
    });
    builder.addCase(getFeeCalculatorThunk.fulfilled, (state, action) => {
      const { data } = action.payload || {};
      state.feeCalculator = data;

      //updateReadOnlyAndComplete
      const newState = updateReadOnlyAndComplete(state, {
        isCompleted: action.payload?.data?.isCompleted,
        status: action.payload?.data?.status,
      });
      state.ui = newState.ui;
      state.isCompleted = newState.isCompleted;
      state.isReadOnly = newState.isReadOnly;

      // Compile fee calculator paths map
      const feeCalculatorPathsMap: Partial<
        Record<
          AnalysisVariable,
          {
            feeCalculatorPath: FieldPath<AnalysisFeeCalculatorDTO>;
            dataValuePath: FieldPath<AnalysisFeeCalculatorDTO>;
            databaseName?: string;
          }
        >
      > = {};
      data?.feeCalculators?.forEach((feeCalculator, index) => {
        const fieldPath =
          `feeCalculators.${index}` as FieldPath<AnalysisFeeCalculatorDTO>;
        const dataValuePath =
          `${fieldPath}.feeCalculatorData.value` as FieldPath<AnalysisFeeCalculatorDTO>;
        feeCalculatorPathsMap[feeCalculator.variableName] = {
          feeCalculatorPath: fieldPath,
          dataValuePath: dataValuePath,
          databaseName: feeCalculator.name,
        };
      });
      state.internal.feeCalculator.feeCalculatorPathsMap =
        feeCalculatorPathsMap;

      state.ui.isLoading = false;
    });
    builder.addCase(getFeeCalculatorThunk.rejected, (state) => {
      state.ui.isLoading = false;
    });

    //updateAnalysisFeeCalculator;
    builder.addCase(updateFeeCalculatorThunk.pending, (state) => {
      state.ui.isSubmitting = true;
    });
    builder.addCase(updateFeeCalculatorThunk.fulfilled, (state) => {
      state.ui.isSubmitting = false;
    });
    builder.addCase(updateFeeCalculatorThunk.rejected, (state) => {
      state.ui.isSubmitting = false;
    });

    //checkPortfolioAssignment
    builder.addCase(checkPortfolioAssignmentThunk.pending, (state) => {
      state.ui.isValidating = true;
    });
    builder.addCase(
      checkPortfolioAssignmentThunk.fulfilled,
      (state, action) => {
        state.ui.isValidating = false;
        if (!state.feeCalculator) return;

        state.feeCalculator = {
          ...state.feeCalculator,
          portfolioSetups: compileOverwritePortfolioValidationResult({
            portfolioSetups: state.feeCalculator?.portfolioSetups || [],
            portfolioValidation: action.payload.data,
            portfolioAssignmentItems: action.meta?.arg?.data?.items,
            portfolios: state.feeCalculator.portfolios ?? [],
            feeCalculators: state.feeCalculator.feeCalculators,
            investmentTypeSelection: action.meta?.arg?.investmentTypeSelection,
            hasIDPSBalance: action.meta?.arg?.data?.hasIDPSBalance ?? true,
            hasSuperBalance: action.meta?.arg?.data?.hasSuperBalance ?? true,
          }),
        };
      },
    );
    builder.addCase(checkPortfolioAssignmentThunk.rejected, (state) => {
      state.ui.isValidating = false;
    });

    // load analysis platforms
    builder.addCase(
      loadPlatformAnalysisPlatformThunk.fulfilled,
      (state, action) => {
        state.analysisPlatforms = action?.payload?.data ?? [];
      },
    );

    // Decision makings
    builder.addCase(updateAnalysisDecisionThunk.fulfilled, (state, action) => {
      const { isSuccess, data } = action.payload;
      const { analysisStep } = action.meta.arg;
      if (!isSuccess || !data) return;

      switch (analysisStep) {
        case AnalysisStep.Feature:
          if (!state.platformAnalysis?.feature?.all?.selectedProducts) break;

          state.platformAnalysis.feature.all.selectedProducts =
            state.platformAnalysis.feature.all.selectedProducts.map((product) =>
              product.id === data.productId
                ? {
                    ...product,
                    decisionStatus: data.decisionStatus,
                    comment: {
                      id: product.comment?.id || "",
                      comment: data.comment || "",
                    },
                  }
                : product,
            );
          break;

        case AnalysisStep.BusinessMetric:
          if (!state.platformAnalysis?.businessMetric?.all?.selectedProducts)
            break;

          state.platformAnalysis.businessMetric.all.selectedProducts =
            state.platformAnalysis.businessMetric.all.selectedProducts.map(
              (product) =>
                product.id === data.productId
                  ? {
                      ...product,
                      decisionStatus: data.decisionStatus,
                      comment: {
                        id: product.comment?.id || "",
                        comment: data.comment || "",
                      },
                    }
                  : product,
            );
          break;

        case AnalysisStep.Fee:
          if (!state.platformAnalysis?.fee?.all.selectedProducts) break;

          state.platformAnalysis.fee.all.selectedProducts =
            state.platformAnalysis.fee.all.selectedProducts.map((product) =>
              product.id === data.productId
                ? {
                    ...product,
                    decisionStatus: data.decisionStatus,
                    comment: {
                      id: product.comment?.id || "",
                      comment: data.comment || "",
                    },
                  }
                : product,
            );
          break;

        default:
          break;
      }
    });
  },
});

export const createPlatformAnalysisThunk = createAsyncThunk(
  `${APIExtRoutes.platformAnalysis}/create`,
  async (setupStep: AnalysisSetupDTO) => {
    return await postPlatformAnalysis(setupStep);
  },
);

export const updatePlatformAnalysisThunk = createAsyncThunk(
  `${APIExtRoutes.platformAnalysis}/update`,
  async (platformAnalysis: AnalysisDTO) => {
    return await putPlatformAnalysis(platformAnalysis);
  },
);

export const getPlatformAnalysisThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisStep,
  async ({
    platformAnalysisId,
    stepId,
  }: {
    platformAnalysisId: string;
    stepId: AnalysisStep;
  }) => {
    const subStep =
      stepId === Feature || stepId === BusinessMetric || stepId === Fee
        ? Analysis
        : undefined;
    const updateCurrentStep = await putPlatformAnalysisCurrentStep(
      platformAnalysisId,
      stepId,
      subStep,
    );
    if (!updateCurrentStep.isSuccess)
      return { ...updateCurrentStep, data: undefined };

    return await getPlatformAnalysisStep(platformAnalysisId, stepId);
  },
);

export const getAnalysisBannerNameThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisBannerName,
  async (platformAnalysisId: string) => {
    return await getPlatformAnalysisBannerName(platformAnalysisId);
  },
);

export const getPlatformAnalysisStepsThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisAllSteps,
  async (platformAnalysisId?: string) => {
    return await getPlatformAnalysisAllSteps(platformAnalysisId);
  },
);

// Feature Analysis
export const getFeatureSelectionsThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisFeatureSelections,
  async ({ platformAnalysisId }: { platformAnalysisId: string }) => {
    const updateCurrentStep = await putPlatformAnalysisCurrentStep(
      platformAnalysisId,
      Feature,
      Selection,
    );
    if (!updateCurrentStep.isSuccess)
      return { ...updateCurrentStep, data: undefined };

    return await getFeatureSelectionStep(platformAnalysisId);
  },
);

export const updateFeatureSelectionThunk = createAsyncThunk(
  `${APIExtRoutes.platformAnalysisFeatureSelections}/update`,
  async ({
    platformAnalysisId,
    featureSelections,
  }: {
    platformAnalysisId: string;
    featureSelections: AnalysisGetFeatureSectionsDTO;
  }) => {
    return await putFeatureSelectionStep(platformAnalysisId, featureSelections);
  },
);

// Business metric
export const getBusinessMetricSelectionsThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisBusinessMetricSelections,
  async ({ platformAnalysisId }: { platformAnalysisId: string }) => {
    const updateCurrentStep = await putPlatformAnalysisCurrentStep(
      platformAnalysisId,
      BusinessMetric,
      Selection,
    );
    if (!updateCurrentStep.isSuccess)
      return { ...updateCurrentStep, data: undefined };

    return await getBusinessMetricSelectionStep(platformAnalysisId);
  },
);

export const updateBusinessMetricSelectionThunk = createAsyncThunk(
  `${APIExtRoutes.platformAnalysisBusinessMetricSelections}/update`,
  async ({
    platformAnalysisId,
    businessMetricSelections,
  }: {
    platformAnalysisId: string;
    businessMetricSelections: BusinessMetricCategories;
  }) => {
    return await putBusinessMetricSelectionStep(
      platformAnalysisId,
      businessMetricSelections,
    );
  },
);
// Fee
export const getFeeCalculatorThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisFeeCalculator,
  async ({ platformAnalysisId }: { platformAnalysisId: string }) => {
    const updateCurrentStep = await putPlatformAnalysisCurrentStep(
      platformAnalysisId,
      Fee,
      Selection,
    );
    if (!updateCurrentStep.isSuccess)
      return { ...updateCurrentStep, data: undefined };

    return await getFeeCalculatorStep(platformAnalysisId);
  },
);
export const updateFeeCalculatorThunk = createAsyncThunk(
  `${APIExtRoutes.platformAnalysisFeeCalculator}/update`,
  async ({
    platformAnalysisId,
    formData,
  }: {
    platformAnalysisId: string;
    formData: AnalysisFeeCalculatorDTO;
  }) => {
    return await putFeeCalculatorStep(platformAnalysisId, formData);
  },
);

// Portfolio
export const createPlatformAnalysisPortfolioThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisCreatePortfolio,
  async ({
    platformAnalysisId,
    formData,
  }: {
    platformAnalysisId: string;
    formData: AnalysisFeePortfolioDTO;
  }) => {
    return await createPlatformAnalysisPortfolio(platformAnalysisId, formData);
  },
);

export const checkPortfolioAssignmentThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisPortfolioAssignmentValidation,
  async ({
    platformAnalysisId,
    data,
  }: {
    platformAnalysisId: string;
    data: PortfolioAssignmentValidationRequestDTO;
    investmentTypeSelection: PortfolioInvestmentTypeSelection;
  }) => {
    return await checkPortfolioAssignment(platformAnalysisId, data);
  },
);

export const updatePlatformAnalysisPortfolioThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisCreatePortfolio,
  async ({
    platformAnalysisId,
    formData,
  }: {
    platformAnalysisId: string;
    formData: AnalysisFeePortfolioDTO;
  }) => {
    return await updatePlatformAnalysisPortfolio(platformAnalysisId, formData);
  },
);

export const getPlatformAnalysisOwnersThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisOwners,
  async (name?: string) => {
    return await getPlatformAnalysisOwners(name);
  },
);

export const checkPlatformAnalysisUniqueNameThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisUniqueName,
  async ({
    firmId,
    name,
    ownerId,
    id,
  }: {
    firmId: string;
    name: string;
    ownerId: string;
    id?: string;
  }) => {
    return await checkAnalysisUniqueName(firmId, name, ownerId, id);
  },
);

export const loadPlatformAnalysisPlatformThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisProducts,
  async ({
    platformAnalysisId,
    filterByProductName,
    configurationVersion,
    apiPath,
  }: {
    platformAnalysisId: string;
    filterByProductName?: string;
    configurationVersion?: number;
    apiPath?: string;
  }) => {
    return await getPlatformAnalysisProducts(
      platformAnalysisId,
      filterByProductName,
      configurationVersion,
      apiPath,
    );
  },
);

export const getFamilyMembersThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisFamilyMembers,
  async ({
    platformAnalysisId,
    familyGroupId,
  }: {
    platformAnalysisId: string;
    familyGroupId: string;
  }) => {
    return getFamilyMembers(platformAnalysisId, familyGroupId);
  },
);

export const getFamilyGroupsThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisFamilyGroups,
  async ({
    platformAnalysisId,
    adviserUserId,
    name,
  }: {
    platformAnalysisId: string;
    adviserUserId: string;
    name: string;
  }) => {
    return getFamilyGroups(platformAnalysisId, adviserUserId, name);
  },
);

export const getAdviserUsersThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisAdviserUsers,
  async ({
    platformAnalysisId,
    name,
  }: {
    platformAnalysisId: string;
    name: string;
  }) => {
    return getAdviserUsers(platformAnalysisId, name);
  },
);

export const updateAnalysisDecisionThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisDecisionMaking,
  async ({
    analysisId,
    analysisStep,
    productId,
    decisionStatus,
    comment,
  }: AnalysisDecisionDTO) => {
    return await updateAnalysisDecision({
      analysisId,
      analysisStep,
      productId,
      decisionStatus,
      comment,
    });
  },
);

export const disqualifyAllPlatformsThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisDisqualify,
  async ({
    analysisId,
    stepId,
  }: {
    analysisId: string;
    stepId: AnalysisStep;
  }) => {
    return await analysisDisqualifyAllPlatforms(analysisId, stepId);
  },
);

export const saveAnalysisFiltersThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisFilter,
  async ({
    analysisId,
    filters,
  }: {
    analysisId: string;
    filters: AnalysisFilterDTO;
  }) => {
    return await savePlatformAnalysisFilters(analysisId, filters);
  },
);

export const updateAnalysisFinalJSONsThunk = createAsyncThunk(
  APIExtRoutes.platformAnalysisFinalJSONs,
  async ({ analysisId }: { analysisId: string }) => {
    return await updateAnalysisFinalJSONs(analysisId);
  },
);

const prepareFeatureAnalysisData = ({
  state,
  isSummary = false,
}: {
  state: PlatformAnalysisStore;
  isSummary?: boolean;
}) => {
  const sections = isSummary
    ? state.platformAnalysis?.summary?.featureAnalysis?.all?.sections || []
    : state.platformAnalysis?.feature?.all?.sections || [];
  const overallScore = isSummary
    ? state.platformAnalysis?.summary?.featureAnalysis?.all?.overallScore
    : state.platformAnalysis?.feature?.all?.overallScore;
  const rows = compileFeatureAnalysisRows({
    sections: sections || [],
    overallScore,
    insertOverallScoreRow: isSummary,
  });

  featureAnalysisTableRowsAdapter.setAll(
    state.ui.featureAnalysis.tableRows,
    rows,
  );
  featureAnalysisTableRowsAdapter.setAll(
    state.ui.featureAnalysis.initialTableRows,
    rows,
  );

  if (!isSummary) return;

  const shortlistedSections =
    state.platformAnalysis?.summary?.featureAnalysis?.shortlisted?.sections;
  const shortlistedOverallScore =
    state.platformAnalysis?.summary?.featureAnalysis?.shortlisted?.overallScore;
  const shortlistedRows = compileBusinessMetricAnalysisRows({
    sections: shortlistedSections || [],
    overallScore: shortlistedOverallScore,
    insertOverallScoreRow: true,
  });
  summaryFeatureAnalysisShortlistedTableRowsAdapter.setAll(
    state.ui.summary.featureAnalysis.shortlistedTableRows,
    shortlistedRows,
  );

  if (!isNil(state.platformAnalysis?.summary?.featureAnalysis?.disqualified)) {
    const chunkData = splitFeatureAnalysis(
      MaximumProductInAppendixTable,
      state.platformAnalysis?.summary?.featureAnalysis?.disqualified,
    );

    const disqualifiedProductChunks: SelectedProductDTO[][] = [];
    const disqualifiedTables: EntityState<FeatureAnalysisRowDTO>[] = [];

    chunkData.forEach((item) => {
      const disqualifiedRows = compileFeatureAnalysisRows({
        sections: item.sections || [],
        overallScore: item.overallScore,
        insertOverallScoreRow: true,
      });

      const entityState =
        summaryFeatureAnalysisDisqualifiedTableRowsAdapter.setAll(
          summaryFeatureAnalysisDisqualifiedTableRowsAdapter.getInitialState(),
          disqualifiedRows,
        );

      disqualifiedTables.push(entityState);
      disqualifiedProductChunks.push(item.selectedProducts);
    });

    state.ui.summary.featureAnalysis.disqualifiedTables = disqualifiedTables;
    state.ui.summary.featureAnalysis.disqualifiedProductChunk =
      disqualifiedProductChunks;
  }

  if (
    !isNil(
      state.platformAnalysis?.summary?.businessMetricAnalysis?.disqualified,
    )
  ) {
    const chunkData = splitFeatureAnalysis(
      MaximumProductInAppendixTable,
      state.platformAnalysis?.summary?.businessMetricAnalysis
        ?.disqualified as AnalysisFeatureAllDataDTO,
    );

    const disqualifiedProductChunks: SelectedProductDTO[][] = [];
    const disqualifiedTables: EntityState<FeatureAnalysisRowDTO>[] = [];

    chunkData.forEach((item) => {
      const disqualifiedRows = compileFeatureAnalysisRows({
        sections: item.sections || [],
        overallScore: item.overallScore,
        insertOverallScoreRow: true,
      });

      const entityState =
        summaryBusinessMetricAnalysisDisqualifiedTableRowsAdapter.setAll(
          summaryBusinessMetricAnalysisDisqualifiedTableRowsAdapter.getInitialState(),
          disqualifiedRows,
        );

      disqualifiedTables.push(entityState);
      disqualifiedProductChunks.push(item.selectedProducts);
    });

    state.ui.summary.businessMetricAnalysis.disqualifiedTables =
      disqualifiedTables;
    state.ui.summary.businessMetricAnalysis.disqualifiedProductChunk =
      disqualifiedProductChunks;
  }
};

const prepareBusinessMetricAnalysisData = ({
  state,
  isSummary = false,
}: {
  state: PlatformAnalysisStore;
  isSummary?: boolean;
}) => {
  const sections = isSummary
    ? state.platformAnalysis?.summary?.businessMetricAnalysis?.all?.sections ||
      []
    : state.platformAnalysis?.businessMetric?.all?.sections || [];
  const rows = compileBusinessMetricAnalysisRows({
    sections: sections || [],
  });

  businessMetricAnalysisTableRowsAdapter.setAll(
    state.ui.businessMetricAnalysis.tableRows,
    rows,
  );
  businessMetricAnalysisTableRowsAdapter.setAll(
    state.ui.businessMetricAnalysis.initialTableRows,
    rows,
  );

  if (!isSummary) return;

  const shortlistedSections =
    state.platformAnalysis?.summary?.businessMetricAnalysis?.shortlisted
      ?.sections;
  const shortlistedOverallScore =
    state.platformAnalysis?.summary?.businessMetricAnalysis?.shortlisted
      ?.overallScore;
  const shortlistedRows = compileBusinessMetricAnalysisRows({
    sections: shortlistedSections || [],
    overallScore: shortlistedOverallScore,
    insertOverallScoreRow: true,
  });
  summaryBusinessMetricAnalysisShortlistedTableRowsAdapter.setAll(
    state.ui.summary.businessMetricAnalysis.shortlistedTableRows,
    shortlistedRows,
  );
};

const prepareFeeAnalysisData = ({
  state,
  payloadData,
  isSummary = false,
}: {
  state: PlatformAnalysisStore;
  payloadData?: AnalysisDTO;
  isSummary?: boolean;
}) => {
  const allHiddenIds: string[] = [];
  const hiddenIds: string[] = [];
  const disqualifiedHiddenIds: string[] = [];

  const allSubProductsData = isSummary
    ? payloadData?.summary?.feeAnalysis?.all?.subProducts
    : payloadData?.fee?.all?.subProducts;
  const shortlistedSubProducts = isSummary
    ? payloadData?.summary?.feeAnalysis?.shortlisted?.subProducts
    : allSubProductsData;
  const disqualifiedSubProducts = isSummary
    ? payloadData?.summary?.feeAnalysis?.disqualified?.subProducts
    : [];

  const shortlistedSubProductIds: string[] =
    shortlistedSubProducts?.map((item) => item.id) ?? [];

  const disqualifiedSubProductIds: string[] =
    disqualifiedSubProducts?.map((item) => item.id) ?? [];

  allSubProductsData?.forEach((subProduct) => {
    const subProductId = subProduct.id;
    if (subProduct.isHidden) {
      allHiddenIds.push(subProductId);

      if (shortlistedSubProductIds.includes(subProductId)) {
        hiddenIds.push(subProductId);
      }
      if (disqualifiedSubProductIds.includes(subProductId)) {
        disqualifiedHiddenIds.push(subProductId);
      }
    }
  });

  state.internal.feeAnalysis.hiddenIds = hiddenIds;
  state.internal.feeAnalysis.disqualifiedHiddenIds = disqualifiedHiddenIds;

  const hiddenIndexes: boolean[] = [];
  const disqualifiedHiddenIndexes: boolean[] = [];
  shortlistedSubProducts?.forEach((subProduct, index) => {
    hiddenIndexes[index] = !!hiddenIds.includes(subProduct.id);
  });

  disqualifiedSubProducts?.forEach((subProduct) => {
    disqualifiedHiddenIndexes.push(
      !!disqualifiedHiddenIds.includes(subProduct.id),
    );
  });
  state.internal.feeAnalysis.hiddenIdsIndexMap = hiddenIndexes;
  state.internal.feeAnalysis.disqualifiedHiddenIdsIndexMap =
    disqualifiedHiddenIndexes;

  const portfolioLevel = isSummary
    ? payloadData?.summary?.feeAnalysis?.all?.portfolioSetup
    : payloadData?.fee?.all?.portfolioSetup;
  state.ui.portfolioLevel = portfolioLevel;
  state.internal.feeCalculator.validatedPortfolioSetupFirstTime =
    portfolioLevel !== FeeCalculatorPortfolioLevel.Family;

  const tableHeatMapMode = isSummary
    ? payloadData?.summary?.feeTableViewMode
    : payloadData?.fee?.feeTableViewMode;
  state.internal.feeAnalysis.heatmapMode =
    tableHeatMapMode ?? FeeHeatmapMode.Detail;

  const tableDisplayStyle = isSummary
    ? payloadData?.summary?.feesDisplayStyle
    : payloadData?.fee?.feesDisplayStyle;
  state.internal.feeAnalysis.displayStyle =
    tableDisplayStyle ?? FeesDisplayStyle.Dollar;

  //Filters
  const investmentMenuData = isSummary
    ? payloadData?.summary?.investmentMenu
    : payloadData?.fee?.investmentMenu;
  const investmentMenuFilter =
    investmentMenuData !== FeeInvestmentMenuOption.AllPlatform
      ? investmentMenuData
      : undefined;
  const disqualifiedInvestmentMenuData =
    payloadData?.summary?.appendixInvestmentMenu ?? undefined;
  const disqualifiedInvestmentMenuFilter =
    disqualifiedInvestmentMenuData !== FeeInvestmentMenuOption.AllPlatform
      ? disqualifiedInvestmentMenuData
      : undefined;

  state.internal.feeAnalysis.filterInvestmentMenuValue = investmentMenuFilter;
  state.internal.feeAnalysis.disqualifiedFilterInvestmentMenuValue =
    disqualifiedInvestmentMenuFilter;

  const feeMissingInvestmentsStyleData = isSummary
    ? payloadData?.summary?.feeMissingInvestmentsStyle
    : payloadData?.fee?.feeMissingInvestmentsStyle;
  const missingInvestmentFilter =
    feeMissingInvestmentsStyleData !== FeeMissingInvestmentsOption.AllPlatform
      ? feeMissingInvestmentsStyleData
      : undefined;
  const disqualifiedFeeMissingInvestmentsStyleData =
    payloadData?.summary?.appendixFeeMissingInvestmentsStyle ?? undefined;
  const disqualifiedMissingInvestmentFilter =
    disqualifiedFeeMissingInvestmentsStyleData !==
    FeeMissingInvestmentsOption.AllPlatform
      ? disqualifiedFeeMissingInvestmentsStyleData
      : undefined;

  state.internal.feeAnalysis.filterMissingInvestmentValue =
    missingInvestmentFilter;
  state.internal.feeAnalysis.disqualifiedFilterMissingInvestmentValue =
    disqualifiedMissingInvestmentFilter;

  //SubProductFiltered
  const allSubProducts = isSummary
    ? shortlistedSubProducts
    : allSubProductsData;
  const subProductFiltered = filterFeeAnalysisSelectedSubProducts(
    allSubProducts || [],
    investmentMenuFilter ?? FeeInvestmentMenuOption.AllPlatform,
    missingInvestmentFilter ?? FeeMissingInvestmentsOption.AllPlatform,
  );
  const disqualifiedSubProductFiltered = filterFeeAnalysisSelectedSubProducts(
    disqualifiedSubProducts || [],
    disqualifiedInvestmentMenuFilter ?? FeeInvestmentMenuOption.AllPlatform,
    disqualifiedMissingInvestmentFilter ??
      FeeMissingInvestmentsOption.AllPlatform,
  );

  state.internal.feeAnalysis.subProductFiltered = subProductFiltered;
  state.internal.feeAnalysis.disqualifiedSubProductFiltered =
    disqualifiedSubProductFiltered || [];

  const filterHiddenIdsIndexMap = buildHiddenIndexesMap(
    allSubProducts || [],
    subProductFiltered,
  );
  state.internal.feeAnalysis.hiddenIdsIndexMap = hiddenIndexes.map(
    (value, index) => value || filterHiddenIdsIndexMap[index],
  );

  const disqualifiedFilterHiddenIdsIndexMap = buildHiddenIndexesMap(
    disqualifiedSubProducts || [],
    disqualifiedSubProductFiltered,
  );
  state.internal.feeAnalysis.disqualifiedHiddenIdsIndexMap =
    disqualifiedHiddenIndexes.map(
      (value, index) => value || disqualifiedFilterHiddenIdsIndexMap[index],
    );

  const excludePerformanceData = isSummary
    ? payloadData?.summary?.excludePerformanceInSMATable
    : payloadData?.fee?.excludePerformanceInSMATable;
  const performanceScope = excludePerformanceData
    ? FeesPerformanceScope.Excluding
    : FeesPerformanceScope.Including;
  state.internal.feeAnalysis.performanceScope = performanceScope;

  const smaDisplayModeData = isSummary
    ? payloadData?.summary?.smaTableViewMode
    : payloadData?.fee?.smaTableViewMode;
  const displayMode = smaDisplayModeData ?? FeesDisplayMode.Summary;
  state.internal.feeAnalysis.displayMode = displayMode;

  const sections = isSummary
    ? state.platformAnalysis?.summary?.smaPortfolioFee?.sections || []
    : state.platformAnalysis?.fee?.smaPortfolioFee?.sections || [];

  const rows = compileFeeAnalysisSmaPortfolioRows({
    sections: sections || [],
    subProducts:
      state.platformAnalysis?.summary?.smaPortfolioFee?.selectedSubProducts,
  });

  feeAnalysisSmaTableRowsAdapter.setAll(
    state.ui.feeAnalysis.smaInitialTableRows,
    rows,
  );
  feeAnalysisSmaTableRowsAdapter.setAll(
    state.ui.feeAnalysis.smaTableRows,
    filterFeeAnalysisTableRows({
      rows,
      performanceScope,
      displayMode,
    }),
  );

  if (isSummary) {
    const sections =
      state.platformAnalysis?.summary?.disqualifiedSmaPortfolioFee?.sections ||
      [];
    const rows = compileFeeAnalysisSmaPortfolioRows({
      sections: sections || [],
    });

    feeAnalysisSmaTableRowsAdapter.setAll(
      state.ui.feeAnalysis.disqualifiedSmaInitialTableRows,
      rows,
    );
    feeAnalysisSmaTableRowsAdapter.setAll(
      state.ui.feeAnalysis.disqualifiedSmaTableRows,
      filterFeeAnalysisTableRows({
        rows,
        performanceScope,
        displayMode,
      }),
    );
  }
};

const prepareProductOrdersData = ({
  state,
}: {
  state: PlatformAnalysisStore;
}): void => {
  if (!state.platformAnalysis?.isReview) return;

  const productOrders: Record<string, Partial<ProductRecommendationDTO>> = {};

  state.platformAnalysis?.summary?.productRecommendations?.forEach(
    (item, index) => {
      productOrders[item.productId] = {
        ...item,
        order: index,
      };
    },
  );

  state.internal.summary.productRecommendations = productOrders;
};

export const {
  updateIsValid,
  updateIsDirty,
  updateIsCompleted,
  updateIsReadOnly,
  updateIsAccessDenied,
  updateIsShowHidden,
  updateIsShowAnalysisScores,
  updateShouldShowSaveProgressWarning,
  updateIsValidPortfolioSetups,
  updateStepHidden,
  updateBannerName,
  updateAnalysisSteps,
  updateFeatureSelectionsGroup,
  updateFeatureSelectionStore,
  updatePlatformAnalysisStore,
  updateStepUnHighlighted,
  updateSelectedStepIndex,
  updateSelectedSubStepIndex,
  updateFeeCalculatorStore,
  updateFilterFeature,
  updateBusinessMetricSelectionStore,
  updateCollapsedSubGroupIds,
  resetAnalysisStore,
  updateReachAnalysisSetupPage,
  updateDisclaimers,
  updateTotalCollapsedFeatureAnalysisRowsAction,
  updateTotalCollapsedBusinessMetricRowsAction,
  updateLineChartColorsAction,
  resetFee,
  setDiscardedStages,
  setFeatureAnalysisRowFilters,
  updateBusinessMetricItemSelectionStore,
  updateFeeCalculatorSectionErrorState,
  setPortfolioLevel,
  setFeeAnalysisHiddenIds,
  setSummaryFeeAnalysisHiddenIds,
  setFeeAnalysisDisqualifiedHiddenIds,
  setFeeAnalysisFilters,
  setFeeAnalysisDisqualifiedFilters,
  setFeeAnalysisHeatmapMode,
  setFeeAnalysisDisplayStyle,
  setFeeAnalysisPerformanceScope,
  setFeeAnalysisDisplayMode,
  setValidatedPortfolioSetupFirstTime,
  setCollapseStateChangedManually,
  setProductRecommendations,
  setSingleProductRecommendation,
  setFeatureAnalysisTableViewMode,
  setSummaryFeeAnalysisFilters,
} = platformAnalysisSlice.actions;

export const featureAnalysisTableRowsSelectors =
  featureAnalysisTableRowsAdapter.getSelectors(
    (state: RootState) => state.platformAnalysis.ui.featureAnalysis.tableRows,
  );
export const businessMetricAnalysisTableRowsSelectors =
  businessMetricAnalysisTableRowsAdapter.getSelectors(
    (state: RootState) =>
      state.platformAnalysis.ui.businessMetricAnalysis.tableRows,
  );
export const feeAnalysisSmaTableRowsSelectors =
  feeAnalysisSmaTableRowsAdapter.getSelectors(
    (state: RootState) => state.platformAnalysis.ui.feeAnalysis.smaTableRows,
  );
export const disqualifiedFeeAnalysisSmaTableRowsSelectors =
  feeAnalysisSmaTableRowsAdapter.getSelectors(
    (state: RootState) =>
      state.platformAnalysis.ui.feeAnalysis.disqualifiedSmaTableRows,
  );
export const summaryFeatureAnalysisShortlistedTableRowsSelectors =
  summaryFeatureAnalysisShortlistedTableRowsAdapter.getSelectors(
    (state: RootState) =>
      state.platformAnalysis.ui.summary.featureAnalysis.shortlistedTableRows,
  );
export const summaryFeatureAnalysisDisqualifiedTableRowsSelectorsByIndex = (
  tableIndex: number,
) =>
  summaryFeatureAnalysisDisqualifiedTableRowsAdapter.getSelectors(
    (state: RootState) =>
      state.platformAnalysis.ui.summary.featureAnalysis.disqualifiedTables[
        tableIndex
      ],
  );
export const summaryBusinessMetricAnalysisShortlistedTableRowsSelectors =
  summaryBusinessMetricAnalysisShortlistedTableRowsAdapter.getSelectors(
    (state: RootState) =>
      state.platformAnalysis.ui.summary.businessMetricAnalysis
        .shortlistedTableRows,
  );
export const summaryBusinessMetricAnalysisDisqualifiedTableRowsSelectorsByIndex =
  (tableIndex: number) =>
    summaryBusinessMetricAnalysisDisqualifiedTableRowsAdapter.getSelectors(
      (state: RootState) =>
        state.platformAnalysis.ui.summary.businessMetricAnalysis
          .disqualifiedTables[tableIndex],
    );

export default platformAnalysisSlice.reducer;
